/**
 * Team Member Analytics Components for CognitiveOS
 * Individual analytics for Team page
 */
import React from 'react';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus,
  Flame, Battery, Compass, Award, ArrowRight,
  AlertCircle, CheckCircle2, Clock, Target, Zap
} from 'lucide-react';
import { Progress } from './ui/progress';

// ===========================
// Burnout Risk Score Card
// ===========================

const getBurnoutConfig = (level) => {
  switch (level) {
    case 'low': return { color: '#10B981', bg: '#D1FAE5', label: 'Low Risk', icon: CheckCircle2 };
    case 'medium': return { color: '#F59E0B', bg: '#FEF3C7', label: 'Medium Risk', icon: AlertCircle };
    case 'high': return { color: '#EF4444', bg: '#FEE2E2', label: 'High Risk', icon: Flame };
    default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Unknown', icon: AlertCircle };
  }
};

const getTrendIcon = (trend) => {
  switch (trend) {
    case 'rising': return { Icon: TrendingUp, color: '#EF4444' };
    case 'decreasing': return { Icon: TrendingDown, color: '#10B981' };
    default: return { Icon: Minus, color: '#6B7280' };
  }
};

export const BurnoutRiskCard = ({ data }) => {
  if (!data) return null;
  
  const config = getBurnoutConfig(data.risk_level);
  const RiskIcon = config.icon;
  const trendConfig = getTrendIcon(data.risk_trend);
  const TrendIcon = trendConfig.Icon;
  
  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E9ECEF' }} data-testid="burnout-risk-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <RiskIcon className="w-4 h-4" style={{ color: config.color }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: '#264653' }}>Burnout Risk</h3>
        </div>
        <div className="flex items-center gap-2">
          <TrendIcon className="w-4 h-4" style={{ color: trendConfig.color }} />
          <span 
            className="px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: config.bg, color: config.color }}
          >
            {config.label}
          </span>
        </div>
      </div>
      
      {/* Risk Score */}
      <div className="mb-4">
        <div className="flex items-baseline gap-1 mb-2">
          <span className="text-3xl font-bold" style={{ color: config.color }}>{data.risk_score}</span>
          <span className="text-sm" style={{ color: '#6B7280' }}>/100</span>
        </div>
        <Progress value={data.risk_score} className="h-1.5" />
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
          <p className="text-lg font-semibold" style={{ color: '#264653' }}>{data.active_tasks}</p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Active Tasks</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
          <p className="text-lg font-semibold" style={{ color: data.lane_mismatch_percentage > 40 ? '#EF4444' : '#264653' }}>
            {data.lane_mismatch_percentage}%
          </p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Lane Mismatch</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
          <p className="text-lg font-semibold" style={{ color: data.weeks_above_capacity > 2 ? '#EF4444' : '#264653' }}>
            {data.weeks_above_capacity}w
          </p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Over Capacity</p>
        </div>
      </div>
      
      {/* Projected Overload */}
      {data.projected_overload_window && (
        <div 
          className="p-3 rounded-lg mb-4 flex items-center gap-2"
          style={{ backgroundColor: config.bg }}
        >
          <Clock className="w-4 h-4" style={{ color: config.color }} />
          <p className="text-xs font-medium" style={{ color: config.color }}>
            {data.projected_overload_window}
          </p>
        </div>
      )}
      
      {/* Recommendations */}
      <div className="border-t pt-3" style={{ borderColor: '#E9ECEF' }}>
        <h4 className="text-xs font-medium mb-2" style={{ color: '#6B7280' }}>RECOMMENDATIONS</h4>
        {data.recommendations?.map((rec, idx) => (
          <div key={idx} className="flex items-start gap-2 mb-1.5">
            <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#2A9D8F' }} />
            <p className="text-xs" style={{ color: '#374151' }}>{rec}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===========================
// Promotion Readiness Card
// ===========================

const getReadinessConfig = (status) => {
  switch (status) {
    case 'ready': return { color: '#10B981', bg: '#D1FAE5', label: 'Ready for Promotion' };
    case 'emerging': return { color: '#F59E0B', bg: '#FEF3C7', label: 'Emerging' };
    case 'not_ready': return { color: '#6B7280', bg: '#F3F4F6', label: 'In Development' };
    default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Unknown' };
  }
};

const getLaneColor = (lane) => {
  const colors = {
    vision: '#8B5CF6', strategy: '#6366F1', creative: '#EC4899',
    marketing: '#F59E0B', revenue: '#10B981', relationships: '#06B6D4',
    culture: '#8B5CF6', operations: '#3B82F6', execution: '#64748B', finance: '#14B8A6'
  };
  return colors[lane] || '#6B7280';
};

export const PromotionReadinessCard = ({ data }) => {
  if (!data) return null;
  
  const config = getReadinessConfig(data.readiness_status);
  const lanePromotions = data.lane_promotions || [];
  
  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E9ECEF' }} data-testid="promotion-readiness-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <Award className="w-4 h-4" style={{ color: config.color }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: '#264653' }}>Promotion Readiness</h3>
        </div>
        <span 
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: config.bg, color: config.color }}
        >
          {config.label}
        </span>
      </div>
      
      {/* Primary Level Progression */}
      <div className="flex items-center justify-center gap-4 mb-4 p-4 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
        <div className="text-center">
          <p className="text-xs mb-1" style={{ color: '#6B7280' }}>Current</p>
          <span className="text-2xl font-bold" style={{ color: '#264653' }}>{data.current_level}</span>
        </div>
        <ArrowRight className="w-6 h-6" style={{ color: config.color }} />
        <div className="text-center">
          <p className="text-xs mb-1" style={{ color: '#6B7280' }}>Target</p>
          <span className="text-2xl font-bold" style={{ color: config.color }}>
            {data.target_level}
          </span>
        </div>
      </div>

      {/* Lane-Specific Promotions */}
      {lanePromotions.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium mb-3" style={{ color: '#6B7280' }}>LANES READY FOR PROMOTION</h4>
          <div className="space-y-2">
            {lanePromotions.map((lp, idx) => {
              const lpConfig = getReadinessConfig(lp.readiness_status);
              const laneColor = getLaneColor(lp.lane);
              return (
                <div 
                  key={idx}
                  className="p-3 rounded-lg border-l-4"
                  style={{ 
                    backgroundColor: '#F9FAFB',
                    borderLeftColor: laneColor
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium capitalize" style={{ color: '#264653' }}>
                      {lp.lane}
                    </span>
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: lpConfig.bg, color: lpConfig.color }}
                    >
                      {lpConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold" style={{ color: '#6B7280' }}>{lp.current_level}</span>
                    <ArrowRight className="w-4 h-4" style={{ color: lpConfig.color }} />
                    <span className="text-lg font-semibold" style={{ color: lpConfig.color }}>{lp.target_level}</span>
                    <span className="ml-auto text-sm font-medium" style={{ color: lpConfig.color }}>
                      {lp.readiness_score}%
                    </span>
                  </div>
                  <Progress value={lp.readiness_score} className="h-1.5 mt-2" />
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Behavior Scores */}
      <div className="space-y-3 mb-4">
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span style={{ color: '#6B7280' }}>Task Reliability (L1→L2)</span>
            <span style={{ color: '#264653' }}>{data.task_completion_reliability}%</span>
          </div>
          <Progress value={data.task_completion_reliability} className="h-1.5" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span style={{ color: '#6B7280' }}>Workflow Ownership (L2→L3)</span>
            <span style={{ color: '#264653' }}>{data.workflow_ownership}%</span>
          </div>
          <Progress value={data.workflow_ownership} className="h-1.5" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span style={{ color: '#6B7280' }}>System Improvement (L3→L4)</span>
            <span style={{ color: '#264653' }}>{data.system_improvement}%</span>
          </div>
          <Progress value={data.system_improvement} className="h-1.5" />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span style={{ color: '#6B7280' }}>Cross-Lane Decisions (L4+)</span>
            <span style={{ color: '#264653' }}>{data.cross_lane_decision_making}%</span>
          </div>
          <Progress value={data.cross_lane_decision_making} className="h-1.5" />
        </div>
      </div>
      
      {/* Development Focus */}
      <div className="border-t pt-3" style={{ borderColor: '#E9ECEF' }}>
        <h4 className="text-xs font-medium mb-2" style={{ color: '#6B7280' }}>DEVELOPMENT FOCUS</h4>
        <p className="text-xs" style={{ color: '#374151' }}>{data.development_focus}</p>
      </div>
    </div>
  );
};

// ===========================
// Lane Drift Alert Card
// ===========================

const getDriftConfig = (risk) => {
  switch (risk) {
    case 'low': return { color: '#10B981', bg: '#D1FAE5', label: 'No Drift' };
    case 'medium': return { color: '#F59E0B', bg: '#FEF3C7', label: 'Moderate Drift' };
    case 'high': return { color: '#EF4444', bg: '#FEE2E2', label: 'High Risk Drift' };
    default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Unknown' };
  }
};

export const LaneDriftCard = ({ data }) => {
  if (!data) return null;
  
  const config = getDriftConfig(data.impact_risk);
  
  // Sort lanes by percentage
  const sortedLanes = Object.entries(data.lane_distribution || {})
    .sort(([, a], [, b]) => b - a);
  
  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E9ECEF' }} data-testid="lane-drift-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <Compass className="w-4 h-4" style={{ color: config.color }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: '#264653' }}>Lane Drift Detection</h3>
        </div>
        {data.has_drift_alert && (
          <span 
            className="px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: config.bg, color: config.color }}
          >
            {config.label}
          </span>
        )}
      </div>
      
      {/* Primary Lane */}
      <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
        <p className="text-xs mb-1" style={{ color: '#6B7280' }}>Primary Lane</p>
        <p className="text-lg font-semibold capitalize" style={{ color: '#264653' }}>{data.primary_lane}</p>
        <p className="text-xs" style={{ color: '#6B7280' }}>
          {data.drift_percentage}% work outside primary lane
        </p>
      </div>
      
      {/* Lane Distribution */}
      <div className="mb-4">
        <h4 className="text-xs font-medium mb-2" style={{ color: '#6B7280' }}>WORKLOAD DISTRIBUTION</h4>
        <div className="space-y-2">
          {sortedLanes.slice(0, 4).map(([lane, pct]) => (
            <div key={lane} className="flex items-center gap-2">
              <span 
                className="text-xs w-20 truncate capitalize"
                style={{ 
                  color: lane === data.primary_lane ? '#2A9D8F' : 
                         lane === data.drift_target_lane ? '#EF4444' : '#6B7280'
                }}
              >
                {lane}
              </span>
              <div className="flex-1 h-2 rounded-full bg-gray-100">
                <div 
                  className="h-full rounded-full"
                  style={{ 
                    width: `${pct}%`,
                    backgroundColor: lane === data.primary_lane ? '#2A9D8F' : 
                                    lane === data.drift_target_lane ? '#EF4444' : '#94A3B8'
                  }}
                />
              </div>
              <span className="text-xs w-10 text-right" style={{ color: '#6B7280' }}>{pct}%</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Alert Details */}
      {data.has_drift_alert && (
        <div 
          className="p-3 rounded-lg mb-4"
          style={{ backgroundColor: config.bg }}
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4" style={{ color: config.color }} />
            <span className="text-xs font-medium" style={{ color: config.color }}>
              Drift Alert: {data.drift_duration_weeks} weeks
            </span>
          </div>
          {data.drifting_to_high_stress && (
            <p className="text-xs ml-6" style={{ color: config.color }}>
              Drifting toward high-stress lane ({data.drift_target_lane})
            </p>
          )}
        </div>
      )}
      
      {/* Corrective Action */}
      {data.corrective_action && (
        <div className="border-t pt-3" style={{ borderColor: '#E9ECEF' }}>
          <div className="flex items-start gap-2">
            <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#2A9D8F' }} />
            <p className="text-xs" style={{ color: '#374151' }}>{data.corrective_action}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ===========================
// Strain Index Mini Card
// ===========================

export const StrainIndexCard = ({ data }) => {
  if (!data) return null;
  
  const getStrainConfig = (level) => {
    switch (level) {
      case 'optimal': return { color: '#10B981', bg: '#D1FAE5', label: 'Optimal' };
      case 'elevated': return { color: '#F59E0B', bg: '#FEF3C7', label: 'Elevated' };
      case 'high': return { color: '#F97316', bg: '#FFEDD5', label: 'High' };
      case 'critical': return { color: '#EF4444', bg: '#FEE2E2', label: 'Critical' };
      default: return { color: '#6B7280', bg: '#F3F4F6', label: 'Unknown' };
    }
  };
  
  const config = getStrainConfig(data.strain_level);
  
  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E9ECEF' }} data-testid="strain-index-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: config.bg }}>
            <Battery className="w-4 h-4" style={{ color: config.color }} />
          </div>
          <h3 className="text-sm font-semibold" style={{ color: '#264653' }}>Cognitive Strain</h3>
        </div>
        <span 
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{ backgroundColor: config.bg, color: config.color }}
        >
          {config.label}
        </span>
      </div>
      
      {/* Strain Score */}
      <div className="text-center mb-4">
        <span className="text-4xl font-bold" style={{ color: config.color }}>{data.strain_score}</span>
        <span className="text-sm ml-1" style={{ color: '#6B7280' }}>/100</span>
      </div>
      
      {/* Quick Metrics */}
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
          <p className="text-lg font-semibold" style={{ color: '#264653' }}>{data.active_task_load}</p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Tasks</p>
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
          <p className="text-lg font-semibold" style={{ color: '#264653' }}>{data.decision_load}</p>
          <p className="text-xs" style={{ color: '#6B7280' }}>Decisions</p>
        </div>
      </div>
    </div>
  );
};

// ===========================
// Combined Member Analytics Panel
// ===========================

export const MemberAnalyticsPanel = ({ analytics, onClose }) => {
  if (!analytics) return null;
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold text-white"
          style={{ backgroundColor: '#2A9D8F' }}
        >
          {analytics.avatar}
        </div>
        <div>
          <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>{analytics.member_name}</h2>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {analytics.title} • {analytics.primary_lane?.charAt(0).toUpperCase() + analytics.primary_lane?.slice(1)} Lane
          </p>
        </div>
      </div>
      
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {analytics.burnout_risk && <BurnoutRiskCard data={analytics.burnout_risk} />}
        {analytics.promotion_readiness && <PromotionReadinessCard data={analytics.promotion_readiness} />}
        {analytics.lane_drift && <LaneDriftCard data={analytics.lane_drift} />}
        {analytics.strain_index && <StrainIndexCard data={analytics.strain_index} />}
      </div>
    </div>
  );
};

export default {
  BurnoutRiskCard,
  PromotionReadinessCard,
  LaneDriftCard,
  StrainIndexCard,
  MemberAnalyticsPanel
};
